/* ==========================================================================
   CASE CARD FEED / LAZY VISUAL COORDINATOR (/database/js/card-feed.js)
   ========================================================================== */

import { state, getCaseModeSelection } from '../state.js?v=20260819-audit2';
import { getFilteredResult } from './runtime-index.js?v=20260819-audit2';
import { cardPool } from '../ui-core.js?v=20260819-audit2';
import { isSingleCaseUrlView } from '../seo.js?v=20260822-audit3';
import { getEffectiveSpecs } from '../compatibility-engine.js?v=20260819-audit2';
import { formatCpuClearance, formatRadiatorSupport, updateCardClearanceGauges } from './card-shared.js?v=20260819-audit2';
import { renderDesktopFeedCard, renderDesktopSingleCard, renderDesktopCompareCard } from './card-desktop.js?v=20260819-audit2';
import { renderMobileCard } from './card-mobile.js?v=20260822-audit3';
import { generateTechnicalSpecsDrawerHtml, renderMicroBlueprint } from './ui-helpers.js?v=20260819-audit2';
import { stableKey } from '../runtime.js?v=20260819-audit2';
import { runtimeNow, debugPerformanceMetric } from './runtime-perf.js?v=20260819-audit2';

let caseImageObserver = null;
let cachedThemeKey = null;
let cachedThemeColors = null;


function promotePriorityCardImage(card) {
  if (!card) return;
  const image = card.querySelector('img.lazy-img-node');
  if (!image) return;

  const pendingSrc = image.getAttribute('data-src');
  if (pendingSrc) {
    image.src = pendingSrc;
    image.removeAttribute('data-src');
  }

  image.loading = 'eager';
  image.fetchPriority = 'high';
  if (caseImageObserver) caseImageObserver.unobserve(image);
}

function cardRenderSignature(c, result, dom, { isMobile, isSelected, isPinned, singleCaseMode, activeModeId }) {
  const hasReport = !!(state.activeAuditReportData && state.activeAuditTargetCaseIdx === result.idx);
  const resultKey = result._compatibilityCacheKey || stableKey([
    result.fits ? 1 : 0,
    ...(result.warns || []),
    '|', ...(result.fails || []),
    '|', result.spineStep?.slotName || '',
    result.activeSpecs?.maxGPU ?? '', result.activeSpecs?.maxGPUT ?? '',
    result.activeSpecs?.maxGPUH ?? '', result.activeSpecs?.maxCPU ?? '', result.activeSpecs?.maxAIO ?? ''
  ]);
  return stableKey([
    'card-render-v2', c._stableKey || c._slug || c.name || result.idx, resultKey,
    activeModeId, isMobile ? 1 : 0, singleCaseMode ? 1 : 0,
    isSelected ? 1 : 0, isPinned ? 1 : 0, hasReport ? 1 : 0,
    dom.cpuH?.value ?? 47, dom.gpuL?.value ?? 304, dom.gpuT?.value ?? 40,
    dom.gpuH?.value ?? 137, dom.radSize?.value ?? 240
  ]);
}

function replaceChildrenIfOrderChanged(container, desiredElements) {
  if (!container) return false;
  const current = container.children;
  let changed = current.length !== desiredElements.length;
  if (!changed) {
    for (let i = 0; i < desiredElements.length; i++) {
      if (current[i] !== desiredElements[i]) { changed = true; break; }
    }
  }
  if (!changed) return false;

  const frag = document.createDocumentFragment();
  desiredElements.forEach(el => frag.appendChild(el));
  container.replaceChildren(frag);
  return true;
}

function getThemeColors() {
  const themeKey = document.documentElement.getAttribute('data-theme') || 'dark';
  if (cachedThemeKey === themeKey && cachedThemeColors) return cachedThemeColors;
  const computed = getComputedStyle(document.documentElement);
  cachedThemeKey = themeKey;
  cachedThemeColors = {
    muted: computed.getPropertyValue('--sff-text-muted').trim() || '#71717a',
    dark: computed.getPropertyValue('--sff-text-dark').trim() || '#f4f4f5',
    blue: computed.getPropertyValue('--sff-sky').trim() || '#38bdf8'
  };
  return cachedThemeColors;
}

export function redrawActiveBlueprints(dom) {
  if (!dom.list) return;
  const themeColors = getThemeColors();
  dom.list.querySelectorAll('.sff-blueprint-canvas').forEach(canvas => {
    const idx = parseInt(canvas.getAttribute('data-idx'), 10);
    const caseObj = state.fetchedCasesGlobal[idx];
    if (!caseObj) return;

    const evalResult = getFilteredResult(idx);
    const spineStep = evalResult ? evalResult.spineStep : null;
    const activeSpecs = evalResult ? evalResult.activeSpecs : null;

    const userCpu = dom.cpuH ? parseInt(dom.cpuH.value, 10) : 47;
    const userGpuLen = dom.gpuL ? parseInt(dom.gpuL.value, 10) : 304;
    renderMicroBlueprint(canvas, caseObj, userCpu, userGpuLen, themeColors, spineStep, activeSpecs);
  });
}

export function createCardElement(c, idx) {
  const isMobile = window.innerWidth <= 1024;
  const isSingleView = isSingleCaseUrlView();
  const isCompareView = state.sffPinnedCases.length === 2;

  if (isMobile) {
    return renderMobileCard(c, idx);
  }
  if (isSingleView) {
    return renderDesktopSingleCard(c, idx);
  }
  if (isCompareView) {
    return renderDesktopCompareCard(c, idx);
  }
  return renderDesktopFeedCard(c, idx);
}

function getCardPoolKey(idx) {
  const isMobile = window.innerWidth <= 1024;
  const viewMode = isMobile
    ? 'mobile'
    : (isSingleCaseUrlView() ? 'single' : (state.sffPinnedCases.length === 2 ? 'compare' : 'feed'));
  return `${idx}:${viewMode}`;
}

export function getOrCreateCard(idx) {
  const poolKey = getCardPoolKey(idx);
  if (cardPool.has(poolKey)) return cardPool.get(poolKey);
  const el = createCardElement(state.fetchedCasesGlobal[idx], idx);
  cardPool.set(poolKey, el);
  return el;
}

export function updateCardState(el, result, dom) {
  const c = state.fetchedCasesGlobal[result.idx];
  if (!el || !c || !result) return;

  const isCompareView = state.sffPinnedCases.length === 2;
  const isMobile = window.innerWidth <= 1024;

  // Desktop single/compare cards use the same workstation shell and must retain
  // .ws-card-feed for their layout rules. Only the dedicated mobile renderer
  // should drop that desktop shell class.
  if (!isMobile) {
    el.classList.add('case-card', 'ws-card-feed');
  } else {
    el.classList.remove('ws-card-feed');
  }

  const badge = el.querySelector('.status-label'), 
        log = el.querySelector('.diagnostic-log'), 
        spineBar = el.querySelector(`#spine-bar-${result.idx}`), 
        spineTitle = el.querySelector(`#spine-title-${result.idx}`), 
        spineSpecs = el.querySelector(`#spine-specs-${result.idx}`);
  
  const validateBtn = el.querySelector('.sff-validate-card-btn');
  const isSelected = state.selectedCaseIdx === result.idx;
  const isPinned = state.sffPinnedCases.includes(result.idx);
  const singleCaseMode = isSingleCaseUrlView();

  const activeModeId = getCaseModeSelection(c, 'default');
  const renderSignature = cardRenderSignature(c, result, dom, {
    isMobile, isSelected, isPinned, singleCaseMode, activeModeId
  });
  if (el.dataset.sffRenderSignature === renderSignature) return;
  el.dataset.sffRenderSignature = renderSignature;

  const activeSpecs = result.activeSpecs || getEffectiveSpecs(c, activeModeId);

  const cpuMetric = el.querySelector('.metric-cpu-val');
  if (cpuMetric) {
    cpuMetric.innerText = formatCpuClearance(c, activeSpecs);
  }
  const gpuMetric = el.querySelector('.metric-gpu-val');
  if (gpuMetric) {
    gpuMetric.innerText = c._isAPU ? 'APU ONLY' : `${activeSpecs.maxGPU} × ${activeSpecs.maxGPUT} × ${activeSpecs.maxGPUH} MM`;
  }
  const aioMetric = el.querySelector('.metric-aio-val');
  if (aioMetric) {
    aioMetric.innerText = formatRadiatorSupport(c, activeSpecs);
  }

  const currentInputs = {
    cpuH: dom.cpuH ? parseInt(dom.cpuH.value, 10) : 47,
    gpuL: dom.gpuL ? parseInt(dom.gpuL.value, 10) : 304,
    gpuT: dom.gpuT ? parseInt(dom.gpuT.value, 10) : 40,
    gpuH: dom.gpuH ? parseInt(dom.gpuH.value, 10) : 137,
    rad: dom.radSize ? parseInt(dom.radSize.value, 10) : 240
  };
  updateCardClearanceGauges(el, c, result, currentInputs);

  const pinBtn = el.querySelector('.sff-pin-card-btn');
  if (pinBtn) {
    pinBtn.classList.toggle('active-pin', isPinned);
    const pinSpan = pinBtn.querySelector('span');
    if (pinSpan) pinSpan.innerText = isPinned ? 'PINNED' : 'PIN';
    const pinSvg = pinBtn.querySelector('svg');
    if (pinSvg) pinSvg.setAttribute('fill', isPinned ? 'currentColor' : 'none');
  }

  const drawerBtn = el.querySelector('.extra-specs-toggle-btn');
  const drawer = el.querySelector('.extra-specs-drawer');

  if (singleCaseMode) {
    if (drawerBtn) {
      drawerBtn.classList.add('hidden-single-view');
      drawerBtn.style.setProperty('display', 'none', 'important');
    }
    if (drawer) {
      drawer.classList.add('active', 'single-case-expanded');
      drawer.style.setProperty('display', 'block', 'important');
      if (drawer.innerHTML.trim() === "") {
        drawer.innerHTML = generateTechnicalSpecsDrawerHtml(c, result.idx);
      }
    }
  } else {
    if (drawerBtn) {
      drawerBtn.classList.remove('hidden-single-view');
      drawerBtn.style.display = '';
    }
    if (drawer) {
      drawer.classList.remove('single-case-expanded');
    }
  }
  
  if (validateBtn) {
    const btnSpan = validateBtn.querySelector('span');
    const hasReport = state.activeAuditReportData && state.activeAuditTargetCaseIdx === result.idx;
    // On mobile the selected card button changes role from case selection to
    // report generation. Keep invalid cards selectable, but once selected never
    // let that same control dispatch a report for a deterministic NO_FIT.
    const mobileReportBlocked = isMobile && isSelected && result.fits !== true;

    validateBtn.disabled = mobileReportBlocked;
    validateBtn.classList.toggle('disabled-btn', mobileReportBlocked);
    validateBtn.setAttribute('aria-disabled', mobileReportBlocked ? 'true' : 'false');

    if (isSelected) {
      validateBtn.classList.add('selected');
      if (btnSpan) {
        btnSpan.innerText = isMobile
          ? (mobileReportBlocked ? 'NO FIT' : (hasReport ? 'VIEW REPORT' : 'GENERATE REPORT'))
          : 'VALIDATE';
      }
    } else {
      validateBtn.classList.remove('selected');
      if (btnSpan) btnSpan.innerText = 'VALIDATE';
    }
  }

  el.classList.toggle('selected-case', isSelected);
  el.classList.toggle('pinned-case', isPinned);
  el.classList.toggle('single-case-focus', singleCaseMode);
  el.classList.toggle('amber-warning', result.fits && result.warns.length > 0);
  el.classList.toggle('incompatible', !result.fits);

  if (result.fits) {
    if (result.warns.length) { 
      if (badge) { badge.className = "status-label status-badge warning"; badge.innerText = "WARNING"; }
      if (log) log.innerHTML = `<span style="color:#fbbf24;font-weight:700;">WARNING:</span> ${result.warns.join(', ').toUpperCase()}`; 
    } else { 
      if (badge) { badge.className = "status-label status-badge fits"; badge.innerText = "FITS"; }
      if (log) log.innerHTML = ''; // Clean FIT is already communicated by badge/border; reserve footer for actionable findings. 
    }
  } else { 
    if (badge) { badge.className = "status-label status-badge nofit"; badge.innerText = "NO FIT"; }
    if (log) log.innerHTML = `<span class="error-text-highlight" style="color:#f43f5e;font-weight:700;">FAILURE:</span> ${result.fails.join(', ').toUpperCase()}`; 
  }
  
  if (result.spineStep && c.hasSpine && spineBar) { 
    spineBar.classList.add('active'); 
    if (spineTitle) spineTitle.innerText = `${result.spineStep.slotName.toUpperCase()} ACTIVE`; 
    if (spineSpecs) spineSpecs.innerHTML = `MAX CPU: ${activeSpecs.maxCPU}MM / GPU THICKNESS: ${activeSpecs.maxGPUT}MM`; 
  } else if (spineBar) {
    spineBar.classList.remove('active');
  }

  const canvas = el.querySelector('.sff-blueprint-canvas');
  if (canvas) {
    const userCpu = dom.cpuH ? parseInt(dom.cpuH.value, 10) : 47;
    const userGpuLen = dom.gpuL ? parseInt(dom.gpuL.value, 10) : 304;
    const themeColors = getThemeColors();

    const rendered = renderMicroBlueprint(canvas, c, userCpu, userGpuLen, themeColors, result.spineStep, activeSpecs);
    if (rendered) {
      canvas.setAttribute('data-rendered', 'true');
    } else {
      canvas.removeAttribute('data-rendered');
      if (caseImageObserver) caseImageObserver.observe(canvas);
    }
  }
}

export function renderVisibleCardFeed(dom) {
  const startedAt = runtimeNow();
  const isComparisonMode = state.sffPinnedCases.length === 2;
  const desiredElements = [];

  if (isComparisonMode) {
    state.sffPinnedCases.forEach(idx => {
      if (state.fetchedCasesGlobal[idx]) {
        const match = getFilteredResult(idx) || { idx, fits: true, warns: [], fails: [], unknowns: [], advisories: [] };
        const el = getOrCreateCard(idx);
        updateCardState(el, match, dom);
        desiredElements.push(el);
      }
    });
    if (dom.stats) dom.stats.innerText = `COMPARISON MODE · 2 CASES PINNED`;
    if (dom.loadMore) dom.loadMore.style.display = 'none';
  } else {
    const end = Math.min(state.visibleLimit, state.filteredResults.length);
    for (let i = 0; i < end; i++) {
      const r = state.filteredResults[i];
      const el = getOrCreateCard(r.idx);
      updateCardState(el, r, dom);
      desiredElements.push(el);
    }
    if (dom.stats) dom.stats.innerText = `${state.totalFitsCount} OF ${state.filteredResults.length} SHOWN FIT · ≤ ${dom.vol ? dom.vol.value : 10}L`;
    if (dom.loadMore) dom.loadMore.style.display = state.filteredResults.length > state.visibleLimit ? 'block' : 'none';
  }

  // The first visible case image is a likely LCP candidate. Request it directly
  // instead of waiting for the IntersectionObserver; all remaining card images stay lazy.
  const priorityCount = isComparisonMode ? Math.min(2, desiredElements.length) : Math.min(1, desiredElements.length);
  for (let i = 0; i < priorityCount; i++) promotePriorityCardImage(desiredElements[i]);

  let reordered = false;
  if (dom.list) {
    reordered = replaceChildrenIfOrderChanged(dom.list, desiredElements);
    if (caseImageObserver && reordered) {
      dom.list.querySelectorAll('.lazy-img-node,.lazy-canvas-node').forEach(n => caseImageObserver.observe(n));
    }
  }

  debugPerformanceMetric('card-feed.render', startedAt, {
    cards: desiredElements.length,
    reordered: reordered ? 1 : 0,
    comparison: isComparisonMode ? 1 : 0
  });
}

export function renderMoreCardFeed(dom) {
  if (!dom.list) return;
  const start = dom.list.children.length;
  const end = Math.min(state.visibleLimit, state.filteredResults.length);
  const frag = document.createDocumentFragment();
  for (let i = start; i < end; i++) {
    const r = state.filteredResults[i]; 
    const el = getOrCreateCard(r.idx);
    updateCardState(el, r, dom); 
    frag.appendChild(el);
    if (caseImageObserver) { el.querySelectorAll('.lazy-img-node,.lazy-canvas-node').forEach(n => caseImageObserver.observe(n)); }
  }
  dom.list.appendChild(frag);
  if (dom.loadMore) dom.loadMore.style.display = state.filteredResults.length > state.visibleLimit ? 'block' : 'none';
}

export function setupLazyLoadingEngine() {
  if ('IntersectionObserver' in window) {
    caseImageObserver = new IntersectionObserver((entries) => {
      const themeColors = getThemeColors();
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const target = entry.target;
        if (target.tagName === 'IMG' && target.getAttribute('data-src')) { 
          target.src = target.getAttribute('data-src'); 
          target.removeAttribute('data-src'); 
          caseImageObserver.unobserve(target);
        } else if (target.tagName === 'CANVAS') {
          const idx = parseInt(target.getAttribute('data-idx'), 10); 
          const caseObj = state.fetchedCasesGlobal[idx];
          if (!caseObj) { caseImageObserver.unobserve(target); return; }

          const evalResult = getFilteredResult(idx);
          const spineStep = evalResult ? evalResult.spineStep : null;
          const activeSpecs = evalResult ? evalResult.activeSpecs : null;

          const userCpu = parseInt(document.getElementById('cpuHeight')?.value || 47, 10);
          const userGpuLen = parseInt(document.getElementById('gpuLength')?.value || 304, 10);
          const rendered = renderMicroBlueprint(target, caseObj, userCpu, userGpuLen, themeColors, spineStep, activeSpecs);
          if (rendered) {
            target.setAttribute('data-rendered', 'true');
            caseImageObserver.unobserve(target);
          }
        }
      });
    }, { root: null, rootMargin: '350px 0px 350px 0px', threshold: 0.01 });
  }
}
